/**
 * Анонсы.
 *
 * Короткое объявление, которое каждый пользователь видит ровно один раз:
 * вошедший — при входе, уже авторизованный — при следующем открытии страницы,
 * новичок — сразу после приветственной инструкции.
 *
 * Черновик и опубликованный различаются флагом: анонс пишут, перечитывают и
 * только потом показывают людям.
 */
import { DeleteButton, EditButton, List, useTable } from "@refinedev/antd";
import type { HttpError } from "@refinedev/core";
import { Space, Table, Tag, Typography } from "antd";

export interface Announcement {
  id: string;
  title?: string;
  body?: string;
  titleEn?: string;
  titleRu?: string;
  bodyEn?: string;
  bodyRu?: string;
  published?: boolean;
  publishedAt?: string;
  coverObjectKey?: string;
  coverImageUrl?: string;
  author?: { id?: string; username?: string };
  createdAt?: string;
}

export function AnnouncementList() {
  const { tableProps } = useTable<Announcement, HttpError>({
    resource: "announcements",
    sorters: { mode: "off" },
  });

  return (
    <List title="Анонсы">
      <Table {...tableProps} rowKey="id">
        <Table.Column
          title="Заголовок"
          dataIndex="titleRu"
          render={(_, item: Announcement) => (
            <Space align="start">
              {item.coverImageUrl && (
                <img
                  src={item.coverImageUrl}
                  alt=""
                  style={{ width: 88, height: 56, objectFit: "cover", borderRadius: 6 }}
                />
              )}
              <Space direction="vertical" size={0}>
                <Typography.Text strong>{item.titleRu || item.titleEn}</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {(item.bodyRu || item.bodyEn || "").slice(0, 120)}
                </Typography.Text>
              </Space>
            </Space>
          )}
        />
        <Table.Column
          title="Состояние"
          dataIndex="published"
          width={140}
          render={(published: boolean) =>
            published ? <Tag color="green">Опубликован</Tag> : <Tag>Черновик</Tag>
          }
        />
        <Table.Column
          title="Опубликован"
          dataIndex="publishedAt"
          width={180}
          // Дата публикации, а не создания: текст пишут заранее, а людям он
          // показывается когда решат.
          render={(value: string, item: Announcement) =>
            value
              ? new Date(value).toLocaleString("ru-RU")
              : `создан ${item.createdAt ? new Date(item.createdAt).toLocaleDateString("ru-RU") : "—"}`
          }
        />
        <Table.Column
          title="Кто"
          dataIndex={["author", "username"]}
          width={150}
          render={(username?: string) => username ?? "—"}
        />
        <Table.Column
          title=""
          width={120}
          render={(_, item: Announcement) => (
            <Space>
              <EditButton hideText size="small" recordItemId={item.id} />
              <DeleteButton hideText size="small" recordItemId={item.id} />
            </Space>
          )}
        />
      </Table>
    </List>
  );
}
